/**
 * Routen, im Code beschrieben.
 *
 * Bewusst ohne dateibasiertes Routing: Der Erzeugungsschritt würde eine weitere
 * nicht eingecheckte Datei (`routeTree.gen.ts`) einführen, die nach dem Klonen
 * erst erzeugt werden muss. Bei sechs Routen ist die Beschreibung im Code
 * kürzer als die Einrichtung des Werkzeugs.
 *
 * Jede Ansicht wird über `lazyRouteComponent` getrennt geladen. Das ist die
 * Umsetzung von „Routen werden je Bereich getrennt geladen; das Startbündel
 * bleibt unter 200 kB" (ADR 0001).
 */
import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
} from "@tanstack/react-router";

import { isAuthenticated } from "@/api/client";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import { t } from "@/i18n";
import { AppShell } from "@/layout/AppShell";

const rootRoute = createRootRoute({
  component: () => <Outlet />,
  notFoundComponent: () => (
    <div className="p-4">
      <EmptyState title={t.errors.notFound} />
    </div>
  ),
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  validateSearch: (search: Record<string, unknown>): { redirect?: string } => ({
    redirect: typeof search.redirect === "string" ? search.redirect : undefined,
  }),
  beforeLoad: () => {
    // Wer angemeldet ist, hat auf der Anmeldeseite nichts zu suchen — sonst
    // entstünde beim Zurücktippen ein zweiter Anmeldevorgang.
    if (isAuthenticated()) throw redirect({ to: "/" });
  },
  component: lazyRouteComponent(() => import("@/routes/login"), "LoginRoute"),
});

/**
 * Pfadlose Hülle: gruppiert alle Ansichten hinter der Anmeldung, ohne ein
 * eigenes Pfadsegment zu erzwingen. Die Prüfung sitzt hier und damit an genau
 * einer Stelle.
 */
const zugangRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/zugang",
  // **Bewusst an `rootRoute` und nicht an `appRoute`:** Diese Ansicht ist der
  // Zugang für Externe ohne Konto (Kapitel 9.4). Hinge sie an `appRoute`,
  // würde der Prüfschritt dort jeden Handwerker auf die Anmeldemaske schicken —
  // also genau die Hürde, die der Einmal-Link umgehen soll.
  component: lazyRouteComponent(() => import("@/routes/zugang"), "ZugangRoute"),
});

/**
 * Einladung einlösen — das erste eigene Passwort.
 *
 * **Bewusst an `rootRoute` und nicht an `appRoute`**, aus demselben Grund wie
 * `/zugang`: Wer hier ankommt, hat noch gar kein Passwort und kann sich
 * folglich nicht anmelden. Hinge die Ansicht hinter der Anmeldeprüfung, schickte
 * sie jeden Eingeladenen auf die Anmeldemaske — also genau vor die Hürde, die
 * dieser Link überwinden soll.
 */
const einladungRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/einladung",
  component: lazyRouteComponent(() => import("@/routes/einladung"), "EinladungRoute"),
});

const appRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "app",
  beforeLoad: ({ location }) => {
    if (!isAuthenticated()) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }
  },
  component: AppShell,
});

const dashboardRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/",
  component: lazyRouteComponent(() => import("@/routes/index"), "DashboardRoute"),
});

// Portfolios stehen vor den Objekten, wie in der Hierarchie aus Kapitel 7.
const portfoliosRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/portfolios",
  component: lazyRouteComponent(() => import("@/routes/portfolios"), "PortfoliosRoute"),
});

const portfolioDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/portfolios/$id",
  component: lazyRouteComponent(() => import("@/routes/portfolios.$id"), "PortfolioDetailRoute"),
});

const propertiesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/properties",
  component: lazyRouteComponent(() => import("@/routes/properties"), "PropertiesRoute"),
});

const propertyDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/properties/$id",
  component: lazyRouteComponent(() => import("@/routes/properties.$id"), "PropertyDetailRoute"),
});

/**
 * Raumansicht nach Kapitel 41.
 *
 * Eigene Route und kein Suchparameter an der Objektseite: Ein Raum muss
 * verlinkbar sein — aus der Vorgangsliste, aus dem Zutrittsprotokoll, aus einem
 * Bericht. Ein Anker in der Objektansicht wäre keine Adresse, sondern eine
 * Sprungmarke in einer Seite, die man erst aufklappen muss.
 */
const roomDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/rooms/$id",
  component: lazyRouteComponent(() => import("@/routes/rooms.$id"), "RoomDetailRoute"),
});

const reportNewRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/report/new",
  // `draft` benennt einen bestimmten angefangenen Entwurf. Kommt aus der
  // Vorgangsübersicht: Wer dort auf „Fortsetzen" tippt, meint genau diesen —
  // ohne den Parameter würde die Maske den zuletzt bearbeiteten öffnen und
  // damit womöglich einen anderen.
  validateSearch: (search: Record<string, unknown>): { draft?: string } => ({
    draft: typeof search.draft === "string" && search.draft !== "" ? search.draft : undefined,
  }),
  component: lazyRouteComponent(() => import("@/routes/report-new"), "ReportNewRoute"),
});

const tasksRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/tasks",
  component: lazyRouteComponent(() => import("@/routes/tasks"), "TasksRoute"),
});

const taskDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/tasks/$id",
  component: lazyRouteComponent(() => import("@/routes/tasks.$id"), "TaskDetailRoute"),
});

const accountRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/account",
  component: lazyRouteComponent(() => import("@/routes/account"), "AccountRoute"),
});

/**
 * Nutzerverwaltung. Neben dem Konto, weil beide dieselbe Frage bedienen — „wer
 * darf hier was" —, nur einmal für einen selbst und einmal für die Organisation.
 */
const usersRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/users",
  component: lazyRouteComponent(() => import("@/routes/users"), "UsersRoute"),
});

const caretakerRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/caretaker",
  component: lazyRouteComponent(() => import("@/routes/caretaker"), "CaretakerRoute"),
});

const inspectionsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inspections",
  component: lazyRouteComponent(() => import("@/routes/inspections"), "InspectionsRoute"),
});

const inspectionDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/inspections/$id",
  component: lazyRouteComponent(
    () => import("@/routes/inspections.$id"),
    "InspectionDetailRoute",
  ),
});

const mediaRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/media",
  component: lazyRouteComponent(() => import("@/routes/media"), "MediaRoute"),
});

const warrantiesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/warranties",
  component: lazyRouteComponent(() => import("@/routes/warranties"), "WarrantiesRoute"),
});

const issuesRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/issues",
  component: lazyRouteComponent(() => import("@/routes/issues"), "IssuesRoute"),
});

const issueDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/issues/$id",
  component: lazyRouteComponent(() => import("@/routes/issues.$id"), "IssueDetailRoute"),
});

const costsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/costs",
  component: lazyRouteComponent(() => import("@/routes/costs"), "CostsRoute"),
});

const offerDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/offers/$id",
  component: lazyRouteComponent(() => import("@/routes/offers.$id"), "OfferDetailRoute"),
});

const calendarRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/calendar",
  component: lazyRouteComponent(() => import("@/routes/calendar"), "CalendarRoute"),
});

const documentsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/documents",
  component: lazyRouteComponent(() => import("@/routes/documents"), "DocumentsRoute"),
});

const projectsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects",
  component: lazyRouteComponent(() => import("@/routes/projects"), "ProjectsRoute"),
});

const projectDetailRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/projects/$id",
  component: lazyRouteComponent(() => import("@/routes/projects.$id"), "ProjectDetailRoute"),
});

const plansRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/plans",
  component: lazyRouteComponent(() => import("@/routes/plans"), "PlansRoute"),
});

const reportsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/reports",
  component: lazyRouteComponent(() => import("@/routes/reports"), "ReportsRoute"),
});

const contactsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/contacts",
  component: lazyRouteComponent(() => import("@/routes/contacts"), "ContactsRoute"),
});

const communicationRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/communication",
  component: lazyRouteComponent(() => import("@/routes/communication"), "CommunicationRoute"),
});

// Testseite der vier Orientierungssystem-Bausteine (Handoff Screen 2a).
// Bewusst nicht in `layout/navigation.ts` verlinkt — nur über die direkte
// URL erreichbar, dient der optischen Prüfung gegen BauSepp.dc.html.
const orientierungssystemTestRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/orientierungssystem-test",
  component: lazyRouteComponent(
    () => import("@/routes/orientierungssystem-test"),
    "OrientierungssystemTestRoute",
  ),
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  zugangRoute,
  einladungRoute,
  appRoute.addChildren([
    dashboardRoute,
    portfoliosRoute,
    portfolioDetailRoute,
    propertiesRoute,
    propertyDetailRoute,
    roomDetailRoute,
    reportNewRoute,
    tasksRoute,
    taskDetailRoute,
    accountRoute,
    usersRoute,
    caretakerRoute,
    inspectionsRoute,
    inspectionDetailRoute,
    mediaRoute,
    warrantiesRoute,
    issuesRoute,
    issueDetailRoute,
    costsRoute,
    offerDetailRoute,
    calendarRoute,
    documentsRoute,
    projectsRoute,
    projectDetailRoute,
    plansRoute,
    reportsRoute,
    contactsRoute,
    communicationRoute,
    orientierungssystemTestRoute,
  ]),
]);

export const router = createRouter({
  routeTree,
  // Beim ersten Anzeichen einer Absicht (Zeigen, Berühren) das Bündel der Route
  // nachladen. Auf dem Telefon ist das der Unterschied zwischen „öffnet sofort"
  // und „lädt nach dem Tippen noch 300 ms".
  defaultPreload: "intent",
  defaultPreloadDelay: 120,
  defaultPendingComponent: () => (
    <div className="flex items-center justify-center p-10 text-ink-muted">
      <Spinner size={28} label={t.common.loading} />
    </div>
  ),
  scrollRestoration: true,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
